import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

let sdkPromise = null;
function loadSquareSdk(url) {
  if (window.Square) return Promise.resolve(window.Square);
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = url; s.async = true;
      s.onload = () => resolve(window.Square);
      s.onerror = () => { sdkPromise = null; reject(new Error("Could not load Square payments")); };
      document.head.appendChild(s);
    });
  }
  return sdkPromise;
}

/** Fetches /payments/config for a specific store (per-store POS credentials). */
export function usePaymentsConfig(storeId) {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    if (!storeId) { setConfig(null); return; }
    setConfig(null);
    api.get(`/payments/config?store_id=${encodeURIComponent(storeId)}`)
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ enabled: false }));
  }, [storeId]);
  return config;
}

/** Mounts a Square card field into `#${containerId}` and returns { ready, error, tokenize }. */
export function useSquareCard(config, containerId, active) {
  const cardRef = useRef(null);
  const paymentsRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!active || !config?.enabled) return undefined;
    let disposed = false;
    (async () => {
      try {
        const Square = await loadSquareSdk(config.sdk_url);
        const payments = Square.payments(config.application_id, config.location_id);
        paymentsRef.current = payments;
        const card = await payments.card();
        if (disposed) return;
        await card.attach(`#${containerId}`);
        cardRef.current = card; setReady(true);
      } catch (e) { if (!disposed) setError(e.message || "Card form failed to load"); }
    })();
    return () => { disposed = true; setReady(false); cardRef.current?.destroy?.(); cardRef.current = null; };
  }, [active, config, containerId]);

  const tokenize = async ({ amount, name, email, phone }) => {
    if (!cardRef.current) throw new Error("Card form is not ready");
    const result = await cardRef.current.tokenize();
    if (result.status !== "OK") throw new Error(result.errors?.map((x) => x.message).join(", ") || "Card validation failed");
    let verificationToken = null;
    try {
      const v = await paymentsRef.current.verifyBuyer(result.token, {
        amount: amount.toFixed(2), currencyCode: "USD", intent: "CHARGE",
        billingContact: { givenName: name?.split(" ")[0] || "", familyName: name?.split(" ").slice(1).join(" ") || "", email: email || undefined, phone: phone || undefined },
      });
      verificationToken = v?.token || null;
    } catch { verificationToken = null; }
    return { sourceId: result.token, verificationToken };
  };

  return { ready, error, tokenize };
}
